// Step 2 — Representatives (Part I, Line 2 + Part II)
import React, { useRef, useState } from 'react';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import type { POAFormState, POARepresentative, StepErrors } from '../../../types/poa';
import {
  DESIGNATION_CODES, US_STATES,
  NON_STATE_JURISDICTIONS, DESIGNATION_FIXED_JURISDICTION,
} from '../poaConstants';
import StaffNameInput, { type PEStaff } from '../StaffNameInput';

const KNOWN_DESIGNATION_CODES = new Set(DESIGNATION_CODES.map((d) => d.value));

interface Props {
  form: POAFormState;
  errors: StepErrors;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof POARepresentative, value: string | boolean) => void;
}

const inputStyle = (hasError?: boolean): React.CSSProperties => ({
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: `1px solid ${hasError ? 'var(--bad)' : 'var(--border)'}`,
  background: 'rgba(255,255,255,.04)', color: 'var(--text)',
  fontSize: 14, outline: 'none',
});

const selectStyle = (hasError?: boolean): React.CSSProperties => ({
  ...inputStyle(hasError),
  cursor: 'pointer',
  background: '#1e2030',
  colorScheme: 'dark',
});

function FieldRow({ label, required, error, hint, children }: {
  label: string; required?: boolean; error?: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
        {label}{required && <span style={{ color: 'var(--bad)', marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {hint && !error && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{hint}</div>}
      {error && <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 3 }}>{error}</div>}
    </div>
  );
}

// ── Lookup loading modal ──────────────────────────────────────────────────────
function RepLookupModal({ staffName, onSkip }: { staffName: string; onSkip: () => void }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(3px)',
    }}>
      <div style={{
        background: 'var(--surface, #1e2030)',
        border: '1px solid var(--border)',
        borderRadius: 16, padding: '40px 44px',
        width: 420, maxWidth: '92vw',
        boxShadow: '0 20px 60px rgba(0,0,0,.7)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
      }}>
        {/* Spinner */}
        <div style={{ marginBottom: 28 }}>
          <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
            <circle cx="26" cy="26" r="21" stroke="rgba(122,162,255,.15)" strokeWidth="4" />
            <circle cx="26" cy="26" r="21"
              stroke="rgba(122,162,255,.85)" strokeWidth="4" strokeLinecap="round"
              strokeDasharray="66 66" strokeDashoffset="33"
              style={{ animation: 'repSpinArc 0.9s linear infinite', transformOrigin: '26px 26px' }}
            />
          </svg>
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>
          Looking up representative credentials
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.6, marginBottom: 10 }}>
          Searching CCH for signer details for
        </div>
        <div style={{
          fontSize: 14, fontWeight: 600, color: 'var(--accent)',
          background: 'rgba(122,162,255,.1)', border: '1px solid rgba(122,162,255,.2)',
          borderRadius: 8, padding: '6px 14px', marginBottom: 24,
        }}>
          {staffName}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.6, marginBottom: 32 }}>
          CAF number, PTIN, designation, and license details will be pre-filled.
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button className="btn" onClick={onSkip} style={{ fontSize: 12, color: 'var(--muted)' }}>
            Skip lookup and fill manually →
          </button>
        </div>
      </div>
      <style>{`
        @keyframes repSpinArc {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ── Rep card ──────────────────────────────────────────────────────────────────
function RepCard({
  rep, index, errors, total, onRemove, onUpdate, onClear,
}: {
  rep: POARepresentative; index: number; errors: StepErrors;
  total: number; onRemove: () => void; onClear: () => void;
  onUpdate: (field: keyof POARepresentative, value: string | boolean) => void;
}) {
  const p = `rep_${index}`;
  const [prefilling,  setPrefilling]  = useState(false);
  const [lookupName,  setLookupName]  = useState('');
  const [fuzzyCandidate, setFuzzyCandidate] = useState<{ name: string; guid: string } | null>(null);
  const skipRef = useRef(false);

  const desDef      = DESIGNATION_CODES.find((d) => d.value === rep.designationCode);
  const needsLicense = desDef?.requiresLicense ?? false;
  const fixedJurisdiction = DESIGNATION_FIXED_JURISDICTION[rep.designationCode];

  function applySignerData(data: Record<string, string>) {
    if (data.address)              onUpdate('address',              data.address);
    if (data.phone)                onUpdate('phone',                data.phone);
    if (data.fax)                  onUpdate('fax',                  data.fax);
    if (data.signerName)           onUpdate('name',                 data.signerName);
    if (data.ptin)                 onUpdate('ptin',                 data.ptin);
    if (data.cafNumber)            onUpdate('cafNumber',            data.cafNumber);
    if (data.licenseNumber)        onUpdate('licenseNumber',        data.licenseNumber);
    if (data.licensingJurisdiction) onUpdate('licensingJurisdiction', data.licensingJurisdiction);
    if (data.designationCode && KNOWN_DESIGNATION_CODES.has(data.designationCode.toLowerCase())) {
      const code = data.designationCode.toLowerCase();
      onUpdate('designationCode', code);
      // CCH may return a state even for a designation the IRS itself enrolls.
      const fixed = DESIGNATION_FIXED_JURISDICTION[code];
      if (fixed) onUpdate('licensingJurisdiction', fixed);
    }
  }

  // Designation drives which licensing authority is even possible, so keep the two
  // in step: snapping to the fixed authority when there is one, and clearing a stale
  // authority when moving back to a state-licensed designation.
  function handleDesignationChange(code: string) {
    onUpdate('designationCode', code);
    const fixed = DESIGNATION_FIXED_JURISDICTION[code];
    if (fixed) {
      onUpdate('licensingJurisdiction', fixed);
    } else if (NON_STATE_JURISDICTIONS.includes(rep.licensingJurisdiction)) {
      onUpdate('licensingJurisdiction', '');
    }
  }

  async function handleStaffAccept(staff: PEStaff) {
    onUpdate('name',          staff.staffName);
    onUpdate('peStaffUser',   staff.staffUser);
    onUpdate('peStaffOffice', staff.staffOffice);
    setFuzzyCandidate(null);
    setLookupName(staff.staffName);
    skipRef.current = false;
    setPrefilling(true);
    try {
      const params = new URLSearchParams({
        staffName:   staff.staffName,
        staffOffice: staff.staffOffice,
        staffUser:   staff.staffUser || '',
      });
      const res = await fetch(`/api/poa-rep-prefill?${params}`);
      if (skipRef.current || !res.ok) return;
      const data = await res.json();
      if (skipRef.current) return;
      if (data.needsConfirmation && data.candidateGuid) {
        setFuzzyCandidate({ name: data.candidateName, guid: data.candidateGuid });
        if (data.address) onUpdate('address', data.address);
        if (data.phone)   onUpdate('phone',   data.phone);
        if (data.fax)     onUpdate('fax',     data.fax);
      } else {
        applySignerData(data);
      }
    } finally {
      setPrefilling(false);
    }
  }

  async function confirmFuzzyMatch() {
    if (!fuzzyCandidate) return;
    const { guid } = fuzzyCandidate;
    setFuzzyCandidate(null);
    skipRef.current = false;
    setPrefilling(true);
    try {
      const params = new URLSearchParams({ staffGuid: guid });
      const res = await fetch(`/api/poa-rep-prefill?${params}`);
      if (skipRef.current || !res.ok) return;
      const data = await res.json();
      if (!skipRef.current) applySignerData(data);
    } finally {
      setPrefilling(false);
    }
  }

  return (
    <>
      {prefilling && (
        <RepLookupModal
          staffName={lookupName}
          onSkip={() => { skipRef.current = true; setPrefilling(false); }}
        />
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            Representative #{index + 1}
            {index < 2 && (
              <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
                IRS sends notices to reps 1–2 only
              </span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn"
              style={{ padding: '4px 8px', fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={onClear}
              title="Clear all fields"
            >
              <RotateCcw size={12} /> Clear
            </button>
            {total > 1 && (
              <button
                className="btn"
                style={{ padding: '4px 8px', fontSize: 12, color: 'var(--bad)', borderColor: 'rgba(255,107,107,.3)', display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={onRemove}
              >
                <Trash2 size={12} /> Remove
              </button>
            )}
          </div>
        </div>

        <div className="bd">
          {/* Fuzzy-match confirmation banner */}
          {fuzzyCandidate && (
            <div style={{
              marginBottom: 12, padding: '8px 12px', borderRadius: 8,
              background: 'rgba(187,154,247,.08)', border: '1px solid rgba(187,154,247,.3)',
              fontSize: 13, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <span style={{ color: 'var(--muted)' }}>
                No exact CCH match found. Best candidate:&nbsp;
                <strong style={{ color: 'var(--text)' }}>{fuzzyCandidate.name}</strong>
              </span>
              <button className="btn" style={{ fontSize: 12, padding: '2px 10px', color: '#9ece6a', borderColor: 'rgba(158,206,106,.35)', background: 'rgba(158,206,106,.07)' }}
                onClick={confirmFuzzyMatch}>
                Yes, use this
              </button>
              <button className="btn" style={{ fontSize: 12, padding: '2px 10px', color: 'var(--muted)' }}
                onClick={() => setFuzzyCandidate(null)}>
                Skip
              </button>
            </div>
          )}

          {/* Part I, Line 2 fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FieldRow label="Full Name" required error={errors[`${p}_name`]}>
              <StaffNameInput
                value={rep.name}
                hasError={!!errors[`${p}_name`]}
                onChange={(name) => onUpdate('name', name)}
                onAccept={handleStaffAccept}
              />
            </FieldRow>
            <FieldRow label="CAF Number">
              <input style={inputStyle()} value={rep.cafNumber}
                placeholder="e.g. 1234-56789R  (or None)"
                onChange={(e) => onUpdate('cafNumber', e.target.value)} />
            </FieldRow>
          </div>

          <FieldRow label="Address" required error={errors[`${p}_address`]}>
            <input style={inputStyle(!!errors[`${p}_address`])} value={rep.address}
              placeholder="e.g. 123 Main Street, Suite 100, Birmingham, AL 35203"
              onChange={(e) => onUpdate('address', e.target.value)} />
          </FieldRow>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <FieldRow label="PTIN" error={errors[`${p}_ptin`]}>
              <input style={inputStyle(!!errors[`${p}_ptin`])} value={rep.ptin}
                placeholder="e.g. P12345678"
                onChange={(e) => onUpdate('ptin', e.target.value)} />
            </FieldRow>
            <FieldRow label="Phone Number">
              <input style={inputStyle()} value={rep.phone}
                placeholder="e.g. 205-555-0100"
                onChange={(e) => onUpdate('phone', e.target.value)} />
            </FieldRow>
            <FieldRow label="Fax Number">
              <input style={inputStyle()} value={rep.fax}
                placeholder="e.g. 205-555-0199"
                onChange={(e) => onUpdate('fax', e.target.value)} />
            </FieldRow>
          </div>

          <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              { field: 'receivesNotices' as const, label: 'Send copies of notices & communications' },
              { field: 'isNewAddress' as const,    label: 'New address' },
              { field: 'isNewPhone' as const,      label: 'New phone' },
              { field: 'isNewFax' as const,        label: 'New fax' },
            ].map(({ field, label }) => (
              <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={rep[field] as boolean}
                  onChange={(e) => onUpdate(field, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>

          {/* Part II — Declaration of Representative */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10, fontWeight: 600, letterSpacing: .5, textTransform: 'uppercase' }}>
              Part II — Declaration of Representative
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <FieldRow label="Designation" required error={errors[`${p}_designation`]}>
                <select style={selectStyle(!!errors[`${p}_designation`])} value={rep.designationCode}
                  onChange={(e) => handleDesignationChange(e.target.value)}>
                  <option value="">— select —</option>
                  {DESIGNATION_CODES.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </FieldRow>

              <FieldRow
                label="Licensing Jurisdiction or Authority"
                required={needsLicense}
                error={errors[`${p}_jurisdiction`]}
                hint={fixedJurisdiction
                  ? `This designation is enrolled by ${fixedJurisdiction}, not by a state.`
                  : undefined}
              >
                {needsLicense ? (
                  <select style={selectStyle(!!errors[`${p}_jurisdiction`])} value={rep.licensingJurisdiction}
                    onChange={(e) => onUpdate('licensingJurisdiction', e.target.value)}>
                    <option value="">— select jurisdiction —</option>
                    <optgroup label="Federal / other licensing authority">
                      {NON_STATE_JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
                    </optgroup>
                    <optgroup label="State or territory">
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                  </select>
                ) : (
                  <input style={inputStyle()} value={rep.licensingJurisdiction} placeholder="Title or relationship"
                    onChange={(e) => onUpdate('licensingJurisdiction', e.target.value)} />
                )}
              </FieldRow>

              <FieldRow
                label="Bar / License / Enrollment Number"
                required={needsLicense}
                error={errors[`${p}_license`]}
              >
                <input style={inputStyle(!!errors[`${p}_license`])} value={rep.licenseNumber}
                  placeholder={needsLicense ? 'e.g. 12345' : ''}
                  onChange={(e) => onUpdate('licenseNumber', e.target.value)} />
              </FieldRow>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function RepresentativesStep({ form, errors, onAdd, onRemove, onUpdate }: Props) {
  function clearRep(repId: string, repOrder: number) {
    const fields: Array<[keyof POARepresentative, string | boolean]> = [
      ['name', ''], ['address', ''], ['cafNumber', ''], ['ptin', ''],
      ['phone', ''], ['fax', ''],
      ['receivesNotices', repOrder === 1],
      ['isNewAddress', false], ['isNewPhone', false], ['isNewFax', false],
      ['designationCode', 'b'],
      ['licensingJurisdiction', 'Alabama'],
      ['licenseNumber', ''],
      ['peStaffUser', ''], ['peStaffOffice', ''],
    ];
    fields.forEach(([field, value]) => onUpdate(repId, field, value));
  }

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
        List up to 4 representatives. The IRS sends notices only to representatives 1 and 2.
        All representatives must sign the declaration (Part II) before the form is submitted to the IRS.
      </p>

      {errors._reps && (
        <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 12, padding: '8px 12px', border: '1px solid var(--bad)', borderRadius: 8, background: 'rgba(255,107,107,.06)' }}>
          {errors._reps}
        </div>
      )}
      {errors._notices && (
        <div style={{ color: 'var(--warn)', fontSize: 13, marginBottom: 12, padding: '8px 12px', border: '1px solid var(--warn)', borderRadius: 8, background: 'rgba(255,209,102,.06)' }}>
          {errors._notices}
        </div>
      )}

      {form.representatives.map((rep, i) => (
        <RepCard
          key={rep.id}
          rep={rep}
          index={i}
          errors={errors}
          total={form.representatives.length}
          onRemove={() => onRemove(rep.id)}
          onClear={() => clearRep(rep.id, rep.repOrder)}
          onUpdate={(field, value) => onUpdate(rep.id, field, value)}
        />
      ))}

      {form.representatives.length < 4 && (
        <button className="btn" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }} onClick={onAdd}>
          <Plus size={14} /> Add Representative
        </button>
      )}
    </div>
  );
}
